import { apiRequest } from '../../../../shared/http/transport.js';

/**
 * The only place the auth feature speaks HTTP.
 *
 * Every call goes through the shared transport, so retry, timeout, credentials, CSRF
 * and error shape are handled in one place and identically for every feature. The old
 * codebase had six separate clients and two of them never checked `response.ok`.
 *
 * No function here contains a business rule. Each one names an endpoint and its shape.
 *
 * Audit references prevented here: store.js #10, media.js #4, integrations.js #13,
 * calendar.jsx #5, training.jsx #2.
 */

/** Label shown in the account screen's device list, for a session created before
 * this app could parse its own reading of the User-Agent header. */
function currentDeviceLabel() {
  return (globalThis.navigator?.userAgent ?? 'Unknown device').slice(0, 60);
}

/**
 * The browser's own IANA zone name - the one location signal a session actually
 * carries, since nothing here looks up an IP or calls an external geo service.
 * `Intl` support is universal in any browser this app runs in, but the call is
 * still guarded: a session is worth creating even on the one browser where it
 * somehow throws, just without a timezone attached to it.
 */
function currentTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  catch { return undefined; }
}

/**
 * Create an account and sign in.
 *
 * @param {{email: string, password: string, displayName: string}} input
 * @returns {Promise<{user: object}>}
 * @throws {import('../../../../shared/http/api-error.js').ApiError}
 */
export function register({ email, password, displayName }) {
  return apiRequest({
    path: '/api/auth/register',
    method: 'POST',
    body: { email, password, displayName, deviceLabel: currentDeviceLabel(), timezone: currentTimezone() },
  });
}

/**
 * Sign in.
 *
 * @param {{email: string, password: string}} input
 * @returns {Promise<{user: object}>}
 * @throws {import('../../../../shared/http/api-error.js').ApiError} UNAUTHENTICATED
 *   for wrong credentials, TOO_MANY_REQUESTS when locked out.
 */
export function login({ email, password }) {
  return apiRequest({
    path: '/api/auth/login',
    method: 'POST',
    body: { email, password, deviceLabel: currentDeviceLabel(), timezone: currentTimezone() },
  });
}

/**
 * Sign out this device.
 *
 * @returns {Promise<null>}
 */
export function logout() {
  return apiRequest({ path: '/api/auth/logout', method: 'POST' });
}

/**
 * Who is signed in, and how long this device stays signed in.
 *
 * Replaces the old `checkAuth`, which read a localStorage flag anyone could set.
 *
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{user: object, session: {id: string, expiresAt: string,
 *   daysRemaining: number}}>}
 */
export function fetchCurrentSession({ signal } = {}) {
  return apiRequest({ path: '/api/auth/session', signal });
}

/**
 * Every device currently signed in.
 *
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{sessions: object[]}>}
 */
export function fetchSessions({ signal } = {}) {
  return apiRequest({ path: '/api/auth/sessions', signal });
}

/**
 * Sign one other device out.
 *
 * @param {{sessionId: string}} input
 * @returns {Promise<null>}
 */
export function revokeSession({ sessionId }) {
  return apiRequest({ path: `/api/auth/sessions/${sessionId}`, method: 'DELETE' });
}

/**
 * Set or clear a session's own label. Cosmetic only - never what decides which
 * session is "this device".
 *
 * @param {{sessionId: string, nickname: string|null}} input
 * @returns {Promise<null>}
 */
export function renameSession({ sessionId, nickname }) {
  return apiRequest({ path: `/api/auth/sessions/${sessionId}/nickname`, method: 'PUT', body: { nickname } });
}

/**
 * Sign every other device out, keeping this one.
 *
 * @returns {Promise<{revokedCount: number}>}
 */
export function revokeOtherSessions() {
  return apiRequest({ path: '/api/auth/sessions/revoke-others', method: 'POST' });
}

/**
 * Change the password. Signs every other device out.
 *
 * @param {{currentPassword: string, newPassword: string}} input
 * @returns {Promise<{revokedCount: number}>}
 */
export function changePassword({ currentPassword, newPassword }) {
  return apiRequest({
    path: '/api/auth/password',
    method: 'POST',
    body: { currentPassword, newPassword, timezone: currentTimezone() },
  });
}

/**
 * Begin a password reset.
 *
 * @param {{email: string}} input
 * @returns {Promise<{accepted: boolean, deliveryAvailable: boolean, message: string}>}
 */
export function requestPasswordReset({ email }) {
  return apiRequest({ path: '/api/auth/password-reset/request', method: 'POST', body: { email } });
}

/**
 * Complete a password reset.
 *
 * @param {{token: string, password: string}} input
 * @returns {Promise<{passwordChanged: boolean}>}
 */
export function confirmPasswordReset({ token, password }) {
  return apiRequest({
    path: '/api/auth/password-reset/confirm',
    method: 'POST',
    body: { token, password },
  });
}

/**
 * Delete the account.
 *
 * @returns {Promise<null>}
 */
export function deleteAccount() {
  return apiRequest({ path: '/api/auth/account', method: 'DELETE' });
}
